/**
 * Serviço de camadas de mapa (KML/INP), por Local — fala com o backend real.
 *
 * Contrato (rota base `local/{localId}/mapa-camadas`):
 *   GET    busca?q=                        -> { resultados: [{ tipo, titulo,
 *                                                subtitulo, lng, lat,
 *                                                equipamentoId, sensorId,
 *                                                camadaId }] }
 *   GET    ``                              -> { arquivos: [{ id, nome, formato,
 *                                                fileName, tamanhoBytes, camadas:
 *                                                [{ id, nome, chave, ordem, cor,
 *                                                icone, visivel }] }] }
 *   GET    arquivo/{id}/geojson            -> [{ chave, rotulo, geoJson }]
 *                                              (sem descricao; feicao com _d
 *                                              busca descricao sob demanda)
 *   GET    camada/{id}/feicao/{i}/descricao -> { descricao }
 *   GET    arquivo/{id}/conteudo           -> text/plain (corpo cru, streamado;
 *                                              formato/fileName em headers)
 *   POST   `` (multipart: arquivo, nome)   -> arquivoId
 *   PATCH  arquivo/{id}    { nome }        -> arquivoId
 *   PUT    arquivo/{id}/conteudo { conteudo, fileName? } -> arquivoId
 *   PATCH  camada/{id}     { nome?, visivel?, cor?, icone? } -> camadaId
 *   DELETE arquivo/{id}                    -> arquivoId
 *
 * O parse KML/INP -> GeoJSON e a reprojeção acontecem no backend; aqui só
 * consumimos GeoJSON pronto. `formato`: 0 = KML, 1 = INP (enum MapaFormato).
 */

import http from '@/utils/http'

export const FORMATO_KML = 0
export const FORMATO_INP = 1

export const ORIGEM_EPANET_MODELO = 'epanet-modelo'
export const ID_ARQUIVO_EPANET_MODELO = 'epanet-modelo'

const base = (localId) => `local/${localId}/mapa-camadas`

export async function listarArquivos(localId) {
  const resposta = await http.get(base(localId))
  return resposta?.arquivos ?? null
}

// Espelho do ModeloInp oficial: metadados + GeoJSON das seções. null se o
// Local não tem modelo (a UI não cria linha).
export function obterModeloEpanet(localId) {
  return http.get(`${base(localId)}/modelo-epanet`, {}, false)
}

export function buscarNoMapa(localId, q) {
  return http.get(`${base(localId)}/busca`, { q }, false)
}

export function obterArquivoGeoJson(localId, arquivoId) {
  return http.get(`${base(localId)}/arquivo/${arquivoId}/geojson`)
}

// Descricao HTML de uma feicao (popup sob demanda). null/vazio se nao houver.
export function obterDescricaoFeicao(localId, camadaId, indice) {
  return http.get(
    `${base(localId)}/camada/${camadaId}/feicao/${indice}/descricao`,
    {},
    false
  )
}

// O backend streama o texto original do arquivo direto no corpo (text/plain),
// sem envelope JSON — por isso getText. Formato/fileName vem em headers, mas o
// editor so precisa do texto (o resto ja veio de listarArquivos).
export async function obterConteudo(localId, arquivoId) {
  const texto = await http.getText(
    `${base(localId)}/arquivo/${arquivoId}/conteudo`
  )
  return texto ?? ''
}

// Cria via multipart: o texto vira um Blob nomeado (o backend lê o IFormFile
// como bytes/UTF-8). Devolve o id do arquivo criado.
export function criarArquivo(localId, { nome, fileName, conteudo }) {
  const form = new FormData()
  form.append('arquivo', new Blob([conteudo], { type: 'text/plain' }), fileName)
  form.append('nome', nome ?? '')
  return http.postForm(base(localId), form)
}

export function renomearArquivo(localId, arquivoId, nome) {
  return http.patch(`${base(localId)}/arquivo/${arquivoId}`, { nome }, false)
}

export function substituirConteudo(localId, arquivoId, { conteudo, fileName }) {
  return http.put(`${base(localId)}/arquivo/${arquivoId}/conteudo`, {
    conteudo,
    fileName: fileName ?? null,
  })
}

export function atualizarCamada(localId, camadaId, patch) {
  return http.patch(`${base(localId)}/camada/${camadaId}`, patch, false)
}

export function removerArquivo(localId, arquivoId) {
  return http.delete(`${base(localId)}/arquivo/${arquivoId}`, arquivoId)
}
